import React, { useMemo } from 'react';

// Components
import InvisibleButton from '../../buttons/InvisibleButton';
import RiskProfileCardContent from './components/RiskProfileCardContent';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Libraries
import classNames from 'classnames';
import { get } from 'lodash';

// Types
import Locale from '@/types/locale';
import RiskProfileItem from '@/types/risk-profile-item';
import { Profile as PartialOmniRiskProfileConfig } from '@/types/risk-profiles/omni/full-omni-risk-profile';
import { RiskProfileConfigRequirements } from '@/types/risk-profiles/risk-profiles';
import { RiskProfileRuleType } from '@/types/risk-profiles';

// Utils
import { getRiskProfileRuleData } from '@/utils/risk-profiles';
import formatRiskProfileDataForStackBar from './utils/format-risk-profile-data-for-stack-bar';

// Legacy Component
import LegacyRiskProfileCard from './LegacyRiskProfileCard';
import User from '@/types/user';
import Nullable from '@/types/nullable';

type Props = {
  riskProfile: PartialOmniRiskProfileConfig | RiskProfileItem;
  className?: string;
  locale: Locale;
  user?: Nullable<User>;
  loading?: boolean;
  hideScoreRange?: boolean;
  onSelect?: () => void;
  requirements?: RiskProfileConfigRequirements[];
  selectedRiskProfile?: RiskProfileItem;
  recommended?: boolean;
};

type LegacyProps = {
  onDelete?: () => void;
  onEdit?: () => void;
  onAttach?: () => void;
};

const RiskProfileCardV2 = (props: Props) => {
  const i18nBasePath = 'RiskProfileCardV2';
  const { riskProfile, className, locale, user, loading, hideScoreRange, onSelect, requirements, recommended, ...rest } = props;
  const { rules } = riskProfile;

  const riskProfileConfigId = get(riskProfile, 'id') ?? get(riskProfile, '_id');

  const onSelectClick = () => onSelect && riskProfile && onSelect();

  const classes = classNames('risk-profile-card-v2', className);

  const stackBarData = useMemo(
    () =>
      rules
        .filter(rule => rule.ruleType === RiskProfileRuleType.AssetAllocation)
        .map(rule => formatRiskProfileDataForStackBar(rule, getRiskProfileRuleData(rule, undefined, undefined, user))),
    [rules, user],
  );

  return (
    <>
      {onSelect ? (
        <InvisibleButton
          className={classes}
          ariaLabel={`Risk profile card for ${riskProfile.title[locale]}`}
          onClick={onSelectClick}
          id={`risk-profile-card-${riskProfileConfigId}`}
        >
          <RiskProfileCardContent
            data={{ riskProfile, recommended, stackBarData, requirements }}
            options={{ hideScoreRange }}
            locale={locale}
            i18nBasePath={i18nBasePath}
          />
        </InvisibleButton>
      ) : (
        <div className={classes}>
          <RiskProfileCardContent
            data={{ riskProfile, recommended, stackBarData, requirements }}
            options={{ hideScoreRange }}
            locale={locale}
            i18nBasePath={i18nBasePath}
          />
        </div>
      )}
    </>
  );
};

const RiskProfileCard = (props: Props & LegacyProps) => {
  const { isLightVersion } = useThemeVersion();
  return isLightVersion ? <RiskProfileCardV2 {...props} /> : <LegacyRiskProfileCard {...props} />;
};

export default RiskProfileCard;
